import { captureException, captureMessage, withScope } from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ChevronLeft, Sim, Watch } from "@/assets/icons/ds";
import { AppearTransition } from "@/components/Interface/AppearTransition/AppearTransition";
import { Button } from "@/components/Interface/Button/Button";
import { CardContainer } from "@/components/Layout/BaseLayouts/CardContainer";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { TwoColumnLayout } from "@/components/Layout/BaseLayouts/TwoColumnLayout";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { useDisplayedSim } from "@/components/Portal/DeviceManagement/hooks/useDisplayedSim.ts";
import { CheckoutPaymentCard } from "@/components/Portal/DeviceManagement/Roaming/components/CheckoutPaymentCard";
import { getHeader } from "@/components/Portal/DeviceManagement/Roaming/roamingUtils";
import { getVoiceOptionEntryFromRateBasket } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionChange/Steps/SubscriptionCheckout";
import {
  SubscriptionInfoBox,
  SubscriptionInfoRow,
} from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionInfoBox/SubscriptionInfoBox";
import {
  DeductionChip,
  PriceRow,
} from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionPrice/SubscriptionPrice";
import { LightboxWrapper } from "@/components/Portal/Documents/PaymentMethods/LightboxWrapper/LightboxWrapper";
import { useAlert } from "@/hooks/useAlert";
import { useHandleError } from "@/hooks/useHandleError";
import { usePaymentMode } from "@/hooks/usePaymentMode";
import { useQueryParam } from "@/hooks/useQueryParam";
import { useSimIdFromQueryParam } from "@/hooks/useSimIdFromQueryParam";
import { ErrorType } from "@/mutator/frontendApiInstance";
import { FullScreenLoadingContext } from "@/provider/FullScreenLoadingProvider/FullScreenLoadingProvider";
import {
  getAvailableSunriseRoamingPackages,
  orderRoamingPackage,
  payTransaction,
  useAddOptionToContract,
  useCreateTransaction,
  useGetProduct,
} from "@/services/api";
import {
  Basket,
  CreateTransactionDatatransOrderType,
  DatatransPaymentResponse,
  DatatransPaymentResponseStatus,
  DrCreditCardCardType,
  PayTransactionParams,
  SignUpResponse,
  SubscriberOrderResponseSubscriberPackageType,
} from "@/services/model";
import { AlertTypes } from "@/utils/atoms";
import { getCreditAmount } from "@/utils/basketUtils";
import { getLocalizedDateString } from "@/utils/dateHelpers";
import { env } from "@/utils/environmentHelpers";
import {
  get3DsBrowserInfo,
  // TODO: Comment back in during 1393
  // isCardholderNameMissing,
} from "@/utils/lightboxHelpers";
import { getNextPaymentDate } from "@/utils/tariffUtils";
import {
  getCurrency,
  getCurrencyFromLookupValue,
} from "@/utils/translationHelpers";

type FormProps = {
  paymentProvider: DrCreditCardCardType;
};

export const AddWatchSimOption: FC = () => {
  const handleError = useHandleError();
  const [, setMode] = useQueryParam("mode");
  const { t, i18n } = useTranslation();
  const { t: tCheckout } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.watchSim.checkout",
  });
  const [simId] = useSimIdFromQueryParam();
  const { isInvoice, isCreditCard, maskedCardNumberString, cardType } =
    usePaymentMode();
  const navigate = useNavigate();
  const showAlert = useAlert();
  const { control, watch } = useForm<FormProps>();

  const [optionBasket, setOptionBasket] = useState<Basket | null>(null);
  const [isSubmittingCheckout, setIsSubmittingCheckout] = useState(false);
  const { setFullScreenLoading } = useContext(FullScreenLoadingContext);

  const { data: simDetail } = useDisplayedSim();

  const serviceId = simDetail?.serviceID;
  const { data: dataProduct, isLoading: isLoadingProduct } = useGetProduct(
    serviceId || 0,
    {
      expanded: true,
      client: Number(env.clientNumericId ?? 1),
    },
    {
      query: {
        enabled: serviceId !== undefined && !isNaN(Number(env.clientNumericId)),
      },
    },
  );
  const voiceOptionEntry = getVoiceOptionEntryFromRateBasket(optionBasket);

  const watchOption = useMemo(
    () =>
      optionBasket?.entries?.[0]?.options?.[0]?.options?.filter(
        (option) =>
          option?.product?.id === 131701 || option?.product?.id === 131702,
      )[0],
    [optionBasket?.entries],
  );
  const watchOptionOneTimeAmount = watchOption?.oneTimeCharges?.[0]?.amount;
  // The one time data product charge, might be discounted as well
  const dataProductOneTimeCharge = useMemo(
    () =>
      voiceOptionEntry?.oneTimeCharges?.filter(
        (value) => value.chargeType?.id === 4,
      ),
    [voiceOptionEntry?.oneTimeCharges],
  );
  const dataProductCharge = useMemo(
    () =>
      dataProduct?.charges?.filter(
        (charge) => charge.id === dataProductOneTimeCharge?.[0]?.charge?.id,
      ),
    [dataProduct?.charges, dataProductOneTimeCharge],
  );
  const queryClient = useQueryClient();

  const goToWatchSimDisplay = useCallback(() => {
    navigate(`../display?simId=${simDetail?.simId}`, { replace: true });
  }, [navigate, simDetail]);

  const handleSuccess = useCallback(async () => {
    // invalidate queries to refetch sim details
    queryClient.invalidateQueries(["/sim/sim"]);
    // Added in order to trigger a refetching in the DeviceManagement
    showAlert({
      type: AlertTypes.success,
      text: t("cockpit.managementTile.roaming.checkout.success"),
    });

    goToWatchSimDisplay();
  }, [goToWatchSimDisplay, queryClient, showAlert, t]);

  const captureError = () => {
    setIsSubmittingCheckout(false);
    handleError(
      "Payment failure",
      `${t("Error.payment.fail")} ${t("Error.payment.pleaseCheckData")}`,
    );
    withScope(function (scope) {
      scope.setFingerprint(["/contract/serviceId/options"]);
      scope.setTag("section", "portal");
      scope.setTag("domain", "CheckoutRoamingOption");
      scope.setTag("endpoint", "addOptionToContract");
      scope.setExtra("simId", simId);
      scope.setContext("paymentContext", {
        isInvoice,
        isCreditCard,
        maskedCardNumberString,
        cardType,
      });
      captureMessage("Payment failure");
    });
  };

  const transactionMutationOptions = {
    onSuccess: (data: DatatransPaymentResponse | SignUpResponse) => {
      // Hide full screen loading
      setFullScreenLoading(false);
      if ("signUpId" in data) {
        handleSuccess();
      } else {
        if (data.status === DatatransPaymentResponseStatus.OK) {
          handleSuccess();
        } else {
          captureError();
        }
      }
    },
    onError: (err: ErrorType<unknown>) => {
      // Hide full screen loading
      setFullScreenLoading(false);
      // If it fails we report the error in sentry
      handleError(err);
      captureException(err);
    },
  };

  // This is used to pay transactions with a saved credit card
  const payAndHandleTransaction = (params: PayTransactionParams) => {
    payTransaction(params)
      .then((data) => {
        setFullScreenLoading(false);
        if (data.status === DatatransPaymentResponseStatus.OK) {
          handleSuccess();
        } else {
          captureError();
        }
      })
      .catch((err) => {
        setFullScreenLoading(false);
        handleError(err);
        captureException(err);
      });
  };

  const discountAmount = optionBasket?.total?.oneTimeCampaign?.amount;
  const credit = getCreditAmount(optionBasket?.balance);
  const creditTimeRemaining =
    optionBasket?.total?.oneTime?.amountDiscounts || 0;
  // substract discount and credit to achieve totalCost - attention! creditTimeRemaining is substracted in backend already
  const totalOnceAmount =
    (optionBasket?.total?.oneTime?.amount || 0) -
    (discountAmount || 0) -
    credit;

  const handleSubmit = () => {
    const basketId = optionBasket?.id;
    if (basketId === undefined) {
      captureMessage("Wanted to submit undefined basket id!");
      handleError("Wanted to submit undefined basket id!");

      return;
    }
    if (totalOnceAmount === undefined) {
      captureMessage("Wanted to charge undefined oneTime amount");
      handleError("Wanted to charge undefined oneTime amount");

      return;
    }
    if (!isSubmittingCheckout) {
      if (isInvoice || isCreditCard) {
        setIsSubmittingCheckout(true); // To prevent double checkout on re-rendering
        setFullScreenLoading(
          true,
          t("cockpit.managementTile.roaming.checkout.loading"),
        );
        // Trigger transaction for this basket
        if (isCreditCard) {
          payAndHandleTransaction({
            simId: parseInt(simId),
            basketId: basketId,
            datatransOrderType:
              CreateTransactionDatatransOrderType.AddOptionToContract,
          });
        }
        // this customer is an invoice customer, we just add option to contract
        else if (isInvoice) {
          addContractCallback(basketId);
        }
      } else {
        // Else we create a transaction, which will in turn open the Lightbox, allow the user to enter payment information
        // and call addOptionToContract backend-side if the payment was successful.
        createTransaction({
          params: {
            simId: parseInt(simId),
            basketId: basketId,
            datatransOrderType:
              CreateTransactionDatatransOrderType.AddOptionToContract,
            // TODO: Comment back in during 1393
            // cardholderName,
            ...get3DsBrowserInfo(),
          },
        });
      }
    }
  };
  const {
    mutate: createTransaction,
    data: datatransRequestDocument,
    isLoading: isCreatingTransaction,
  } = useCreateTransaction();

  // TODO: Comment back in during 1393
  // const [cardholderName, setCardholderName] = useState("");
  const {
    mutate: addOptionToContract,
    isLoading: isAddingOptionLoading,
    isSuccess: isAddingOptionDone,
  } = useAddOptionToContract({
    mutation: transactionMutationOptions,
  });
  const addContractCallback = useCallback(
    (basketId: number) => {
      if (optionBasket) {
        addOptionToContract({
          serviceId: parseInt(simId),
          data: {
            header: getHeader(),
            basket: optionBasket,
            basketId,
          },
        });
      }
    },
    [addOptionToContract, optionBasket, simId],
  );

  useEffect(() => {
    // Only initialize a new basket if theres none in the optionBasketAtom
    if (!optionBasket) {
      if (typeof serviceId === "number" && simId) {
        // Get available packages for this sim
        getAvailableSunriseRoamingPackages(parseInt(simId), {
          packageType: SubscriberOrderResponseSubscriberPackageType.Watch_Sim,
        }).then((data) => {
          const watchPackageId = data?.subscriberPackagesWatch?.[0].id;
          if (watchPackageId === undefined) {
            const e = new Error(
              `No watch package found for serviceId ${serviceId}`,
            );
            captureException(e);
            handleError(e);
            setMode("", { replace: true });
          } else {
            orderRoamingPackage(parseInt(simId), {
              optionId: serviceId,
              watchId: watchPackageId,
            }).then((data) => {
              setOptionBasket(data?.basket || null);
            });
          }
        });
      }
    }
  }, [optionBasket, serviceId, simId, setMode, handleError]);

  const watchedPaymentProvider = watch("paymentProvider");
  const disableSuccessButton =
    isCreatingTransaction ||
    isAddingOptionLoading ||
    isAddingOptionDone ||
    !(watchedPaymentProvider || isInvoice || isCreditCard);
  // TODO: Comment back in during 1393
  // || isCardholderNameMissing(watchedPaymentProvider, cardholderName)
  const currency = getCurrencyFromLookupValue(
    optionBasket?.currency || { id: 1 },
  );
  const isLoading =
    isLoadingProduct ||
    optionBasket === null ||
    isCreatingTransaction ||
    isAddingOptionLoading;

  const flatName = dataProduct?.baseDisplayValue;
  const flatPrice = dataProductCharge?.[0]?.amount;
  // The campaign discount's own name (e.g. "Friends with Benefits 10%");
  // the generic "Rabatt" only when the basket carries none.
  const discountName =
    voiceOptionEntry?.discounts?.[0]?.discountName ??
    t("Onboarding.sections.checkout.summary.discount");
  const totalRecurringAmount = optionBasket?.total?.recurring?.amount;
  const nextBillDate =
    simDetail?.remainingDays != null
      ? getLocalizedDateString(
          getNextPaymentDate(simDetail.remainingDays),
          i18n.language,
        )
      : undefined;
  const billingDays = simDetail?.minContractDuration ?? 30;

  // simDetail should always be present. This is to prevent TypeScript warnings.
  if (!simDetail) {
    return null;
  }

  return (
    <section className="w-full" data-testid="add-watch-sim-option">
      <CardContainer
        variant="ivory"
        className="flex w-full flex-col gap-6 rounded-lg p-6"
      >
        <Button
          dataTestid="watch-sim-checkout-back"
          variant="tertiary"
          ariaLabel={t("common.buttons.back")}
          icon={<ChevronLeft />}
          onClick={goToWatchSimDisplay}
        />

        <div className="flex w-full flex-col gap-4">
          <h1 className="text-headline-xl font-semibold leading-xs tracking-[-0.02em] text-foreground-primary">
            {tCheckout("title")}
          </h1>
          <p className="text-body-base leading-none text-foreground-secondary">
            <Trans
              t={t}
              values={{ count: simDetail.remainingDays ?? 1 }}
              components={[<span key="boldTime" className="font-semibold" />]}
            >
              cockpit.managementTile.watchSim.checkout.autoRenewal.hint
            </Trans>
          </p>
        </div>

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <AppearTransition>
            <TwoColumnLayout
              independentColumns
              rows={[
                {
                  left: (
                    <ProfileCard
                      dataTestid="watch-sim-checkout-tariff"
                      variant="white"
                      title={t(
                        "portal:cockpit.managementTile.tariff.yourTariff",
                      )}
                    >
                      <div className="flex w-full flex-col gap-3">
                        <div className="flex h-14 w-full flex-col justify-center rounded-md bg-surface-quaternary px-5 text-foreground-tertiary">
                          <p className="text-body-xs leading-md">
                            {tCheckout("deviceAssignment")}
                          </p>
                          <p className="truncate-safe text-body-base font-semibold leading-md">
                            {simDetail.alias ?? flatName}
                          </p>
                        </div>

                        <div className="flex w-full flex-col gap-2">
                          <SubscriptionInfoBox>
                            <SubscriptionInfoRow
                              icon={<Sim className="size-6" />}
                              title={flatName ?? ""}
                            />
                          </SubscriptionInfoBox>

                          <SubscriptionInfoBox>
                            <SubscriptionInfoRow
                              icon={<Watch className="size-6" />}
                              title={t(
                                "portal:cockpit.managementTile.watchSim.display.watchSIM",
                              )}
                            />
                          </SubscriptionInfoBox>
                        </div>
                      </div>
                    </ProfileCard>
                  ),
                  right: (
                    <ProfileCard
                      dataTestid="watch-sim-checkout-due"
                      className="self-start"
                      variant="white"
                      title={tCheckout("nowToPay")}
                    >
                      <div className="flex w-full flex-col">
                        <PriceRow
                          withBackdrop
                          variant="strong"
                          label={flatName}
                          value={
                            flatPrice != null
                              ? getCurrency(flatPrice, currency)
                              : ""
                          }
                        />

                        {!!discountAmount && discountAmount > 0 && (
                          <PriceRow
                            variant="strong-xs"
                            label={discountName}
                            value={
                              <span data-testid="watch-sim-checkout-discount">
                                <DeductionChip amount={discountAmount} />
                              </span>
                            }
                          />
                        )}

                        {watchOptionOneTimeAmount != null && (
                          <PriceRow
                            label={t(
                              "portal:cockpit.managementTile.watchSim.display.watchSIM",
                            )}
                            value={
                              <span data-testid="watch-sim-checkout-watch-option">
                                {getCurrency(
                                  watchOptionOneTimeAmount,
                                  currency,
                                )}
                              </span>
                            }
                          />
                        )}

                        {creditTimeRemaining > 0 && (
                          <PriceRow
                            variant="strong-xs"
                            label={tCheckout("creditRemainingTime")}
                            value={
                              <span data-testid="watch-sim-checkout-credit-time-remaining">
                                <DeductionChip amount={creditTimeRemaining} />
                              </span>
                            }
                          />
                        )}

                        {credit > 0 && (
                          <PriceRow
                            withDivider
                            variant="strong-xs"
                            label={tCheckout("creditOther")}
                            value={
                              <span data-testid="watch-sim-checkout-credit">
                                <DeductionChip amount={credit} />
                              </span>
                            }
                          />
                        )}

                        <PriceRow
                          variant="strong"
                          label={
                            <>
                              <span>{tCheckout("sum")}</span>
                              <br />
                              <span className="text-body-xs font-regular leading-md">
                                {t(
                                  "Onboarding.sections.checkout.summary.taxIncluded",
                                )}
                              </span>
                            </>
                          }
                          value={
                            <>
                              <span data-testid="watch-sim-checkout-total">
                                {getCurrency(
                                  Math.max(totalOnceAmount, 0),
                                  currency,
                                )}
                              </span>
                              <span className="text-body-xs font-regular leading-md">
                                {tCheckout("today")}
                              </span>
                            </>
                          }
                        />

                        {totalRecurringAmount != null && (
                          <PriceRow
                            withBackdrop
                            variant="strong"
                            label={
                              <>
                                <span>{tCheckout("thereafter")}</span>
                                <br />
                                {nextBillDate && (
                                  <span className="text-body-xs font-regular leading-md">
                                    {tCheckout("dueOn", {
                                      date: nextBillDate,
                                    })}
                                  </span>
                                )}
                              </>
                            }
                            value={
                              <>
                                <span data-testid="watch-sim-checkout-recurring">
                                  {getCurrency(totalRecurringAmount, currency)}
                                </span>
                                <span className="text-body-xs font-regular leading-md">
                                  {t(
                                    "portal:cockpit.managementTile.tariff.payInterval",
                                    {
                                      days: billingDays,
                                    },
                                  )}
                                </span>
                              </>
                            }
                          />
                        )}
                      </div>
                    </ProfileCard>
                  ),
                },
                {
                  left: (
                    <CheckoutPaymentCard
                      dataTestid="watch-sim-checkout-payment"
                      keyPrefix="cockpit.managementTile.watchSim.checkout.paymentMethod"
                      isInvoice={isInvoice}
                      isCreditCard={!!isCreditCard}
                      cardType={cardType}
                      maskedCardNumberString={maskedCardNumberString}
                      control={control}
                    />
                  ),
                },
              ]}
            />

            <LightboxWrapper
              paymentProvider={watchedPaymentProvider}
              transactionId={datatransRequestDocument?.refNo}
              sign={datatransRequestDocument?.sign}
              enabled={datatransRequestDocument !== undefined}
              // TODO: Comment back in during 1393
              // cardholderName={cardholderName}
              // onCardholderNameChange={setCardholderName}
            />

            <div className="mt-6 flex w-full justify-end">
              <Button
                dataTestid="watch-sim-checkout-submit"
                disabled={disableSuccessButton}
                onClick={handleSubmit}
              >
                {tCheckout("confirm")}
              </Button>
            </div>
          </AppearTransition>
        )}
      </CardContainer>
    </section>
  );
};
